import { getClassLevel, normalizeClassId } from './classIdentity.mjs';
import { channelDivinityUses } from './paladin.mjs';

// 2024 Channel Divinity (SRD 5.2) is one resource per class: its uses come from that class's table, you regain
// one expended use when you finish a Short Rest and all of them when you finish a Long Rest. State lives at
// `classState.<class>.channelDivinity` as uses spent, so a new level grows the remainder.
export const CHANNEL_DIVINITY_CLASSES = Object.freeze({
  paladin: channelDivinityUses,
  // Cleric table (2024): 2 uses from level 2, 3 from 6, 4 from 18.
  cleric: level => (level < 2 ? 0 : level < 6 ? 2 : level < 18 ? 3 : 4),
});

export const getChannelDivinityProfile = (character, classId) => {
  const id = normalizeClassId(classId);
  const usesFor = CHANNEL_DIVINITY_CLASSES[id];
  const level = usesFor ? getClassLevel(character, id) : 0;
  const maximum = usesFor ? usesFor(level) : 0;
  const state = character?.classState?.[id]?.channelDivinity || {};
  const spent = Math.min(maximum, Math.max(0, Math.floor(Number(state.spent) || 0)));
  return { classId: id, level, maximum, spent, remaining: maximum - spent, revision: Math.max(0, Number(state.revision) || 0) };
};

/** Classes with Channel Divinity uses for this character. */
export const channelDivinityClasses = character => Object.keys(CHANNEL_DIVINITY_CLASSES)
  .filter(classId => getChannelDivinityProfile(character, classId).maximum > 0);

/** A Short Rest regains one use and a Long Rest all of them; null means nothing changes. */
export const channelDivinityRestState = (character, classId, type) => {
  const profile = getChannelDivinityProfile(character, classId);
  if (!profile.maximum || !['short', 'long'].includes(type)) return null;
  const spent = type === 'long' ? 0 : Math.max(0, profile.spent - 1);
  if (spent === profile.spent && type === 'short') return null;
  return { spent, revision: profile.revision + 1 };
};

/** Merge a server state unless the sheet already holds a newer revision. */
export const applyChannelDivinityUpdate = (character, classId, channelDivinity, changes = {}) => {
  const id = normalizeClassId(classId);
  if (!channelDivinity || Number(character?.classState?.[id]?.channelDivinity?.revision || 0) > Number(channelDivinity.revision || 0)) return character;
  return { ...character, ...changes, classState: { ...character?.classState, [id]: { ...character?.classState?.[id], channelDivinity } } };
};
